import { PHASE_PRODUCTION_BUILD } from "next/constants";

// Build-time cache: avoids re-fetching identical GET requests across many static pages.
// Stores the parsed body as an ArrayBuffer so each caller gets a fresh Response clone.
// ArrayBuffer is explicitly BodyInit — Buffer and Uint8Array are not assignable in strict TS.
// The Map is intentionally never cleared: the build process exits when done, so there is no unbounded growth risk.
const buildCache = new Map<
  string,
  { status: number; headers: [string, string][]; body: ArrayBuffer }
>();

const isBuildTime = () => process.env.NEXT_PHASE === PHASE_PRODUCTION_BUILD;

// Only cache endpoints that are identical across all pages (menus, options, etc.).
// Page-specific requests (slug lookups, media, etc.) are excluded to prevent unbounded memory growth.
const SHARED_ENDPOINT_PATTERNS = [
  "/menus/v1/menus/",
  "/aunds/theme-options",
  "/aunds/template-options",
  "/aunds/template-css",
  "/aunds/template-routing",
  "/getFrontpage",
  "/acf/v3/options/category-overview-settings",
];

const isSharedEndpoint = (url: string): boolean =>
  SHARED_ENDPOINT_PATTERNS.some((pattern) => url.includes(pattern));

const cacheKey = (
  input: RequestInfo | URL,
  init?: RequestInit,
): string | null => {
  const method = init?.method?.toUpperCase() ?? "GET";
  if (method !== "GET") return null;
  const url =
    typeof input === "string"
      ? input
      : input instanceof URL
        ? input.href
        : (input as Request).url;
  if (!isSharedEndpoint(url)) return null;
  return url;
};

export const fetchFromVolatileServer = async (
  input: RequestInfo | URL,
  init?: RequestInit,
  retries: number = 3,
): Promise<Response> => {
  const key = isBuildTime() ? cacheKey(input, init) : null;

  if (key && buildCache.has(key)) {
    const cached = buildCache.get(key)!;
    return new Response(cached.body, {
      status: cached.status,
      headers: cached.headers,
    });
  }

  try {
    const response = await fetch(input, init);
    if (!response.ok && response.status >= 500 && retries > 0) {
      //? This is useful for debugging in the pipeline, so it should stay
      // eslint-disable-next-line no-console
      console.log(
        "fetchFromVolatileServer 5xx retry",
        retries,
        response.status,
        input,
      );
      await new Promise((res) => setTimeout(res, 1000));
      return fetchFromVolatileServer(input, init, retries - 1);
    }
    if (key && response.ok) {
      const body = await response.arrayBuffer();
      const headers: [string, string][] = [...response.headers.entries()];
      buildCache.set(key, { status: response.status, headers, body });
      return new Response(body, { status: response.status, headers });
    }
    return response;
  } catch (error) {
    //? This is useful for debugging in the pipeline, so it should stay
    // eslint-disable-next-line no-console
    console.log("fetchFromVolatileServer network error", retries, input);
    if (retries > 0) {
      await new Promise((res) => setTimeout(res, 1000));
      return fetchFromVolatileServer(input, init, retries - 1);
    }
    throw error;
  }
};
